// Reads top-window scope so the editor chrome and canvas iframe share
// the same source (DOMQL clones context.globalScope per create()).
import { changeGlobalTheme } from 'smbls'
// Bare shared-library import, not a deep functions/canvasBackground.js
// subpath — a per-file deep specifier into @symbo-ls/workspace-shared cannot
// resolve through the platform's public importmap (tickets/execution-board.md
// §1, same fix as workspace/app.js). Deferred call wrapper, not a
// module-scope destructure, per the a5d0a2aa barrel-cycle lesson.
import _wsShared from '@symbo-ls/workspace-shared'
const canvasSyncCanvasBackgroundForTheme = (...args) =>
  _wsShared.functions.syncCanvasBackgroundForTheme(...args)
import { resolveTopWindow } from '../globalScope.js'

export function canvasGetCanvasScope(childKey) {
  const topWindow = resolveTopWindow()
  const scope = topWindow?.__canvasGlobalScope || this.context?.globalScope
  return childKey ? scope?.[childKey] : scope
}

export function canvasGetCanvasPositions() {
  const scope = this.call('getCanvasScope')
  return scope?.positions || { x: 0, y: 0, scale: 0.35 }
}

// Zoom methods. When the canvas v2 surface is active these branch off
// to the v2 viewport pipeline so the existing outer chrome (shared
// Zoombar, command bar, etc.) drives v2 transparently.

// The math lives in functions/canvas/state.js `zoomAroundCenter` (one
// source — the panzoom shim's camera bridge and the Zoombar presets use the
// same function); this is the thin method-side entry.
function v2ZoomAroundCenter(el, factor) {
  return el.call('zoomAroundCenter', factor)
}

export function canvasZoomToNode(targetNode, opts = {}) {
  const scope = this.call('getCanvasScope')
  if (!scope?.panzoomInstance || !targetNode) return
  const panzoom = scope.panzoomInstance

  if (opts.useZoom) {
    const { x, y } = panzoom.getTransform()
    panzoom.zoomAbs(x, y, opts.zoom || 0.65)
  }

  setTimeout(() => {
    const node = targetNode.node || targetNode
    const { x, y, width, height } = node.getBoundingClientRect()
    const panToX = x - (window.innerWidth - width) / 2
    const panToY = y - (window.innerHeight - height) / 2
    panzoom.moveBy(-panToX, -panToY, true)
  }, 50)
}

export function zoomIn() {
  if (this.call('isCanvasV2')) return v2ZoomAroundCenter(this, 1.25)
  const scope = this.call('getCanvasScope')
  if (!scope?.panzoomInstance) return
  const { x, y } = scope.positions
  scope.panzoomInstance.smoothZoom(x + window.innerWidth / 2, y + window.innerHeight / 2, 1.25)
}

export function zoomOut() {
  if (this.call('isCanvasV2')) return v2ZoomAroundCenter(this, 0.8)
  const scope = this.call('getCanvasScope')
  if (!scope?.panzoomInstance) return
  const { x, y } = scope.positions
  scope.panzoomInstance.smoothZoom(x + window.innerWidth / 2, y + window.innerHeight / 2, 0.75)
}

export function zoomReset() {
  if (this.call('isCanvasV2')) return this.call('resetCanvasView')
  const scope = this.call('getCanvasScope')
  if (!scope?.panzoomInstance) return
  const { x, y } = scope.positions
  scope.panzoomInstance.smoothZoomAbs(x + window.innerWidth / 2, y + window.innerHeight / 2, 1)
}

export function getZoomLevel() {
  if (this.call('isCanvasV2')) return this.call('getCanvasV2')?.viewport?.scale || 0.35
  const scope = this.call('getCanvasScope')
  return scope?.positions?.scale || 0.35
}

// Navigation
export function findBoxByRoute(route) {
  const root = this.__ref?.root || this
  const collection = root?.CanvasScene?.Collection
  if (!collection?.node) return null
  const key = route === '/' ? '/' : '/' + route.split('/').filter(Boolean)[0]
  const children = collection.node.children
  for (let i = 0; i < children.length; i++) {
    if (children[i].ref?.scope?.key === key) return children[i].ref
  }
  return null
}

export function navigateCanvas(path) {
  const box = this.call('findBoxByRoute', path)
  if (box) this.call('zoomToNode', box)
}

// Selection / Hover
export function canvasActivateHovered(key) {
  const scope = this.call('getCanvasScope')
  if (!scope) return
  if (key === null) {
    scope.hovered = null
    scope.hoveredKey = null
  } else {
    scope.hovered = this
    scope.hoveredKey = typeof key === 'string' ? key : this.state?.key
  }
}

export function canvasActivateSelected(key) {
  if (this.call('isCanvasV2')) {
    if (key === null) this.call('clearSelection')
    else this.call('selectOne', typeof key === 'string' ? key : this.state?.key)
    return
  }
  const scope = this.call('getCanvasScope')
  if (!scope) return
  if (key === null) {
    scope.selected = null
    scope.selectedKey = null
  } else {
    scope.selected = this
    scope.selectedKey = typeof key === 'string' ? key : this.state?.key
  }
}

export function canvasGetSelected() {
  if (this.call('isCanvasV2')) {
    const key = this.call('getCanvasV2')?.selection?.primary
    if (!key) return null
    const items = this.getRoot?.()?.lookdown?.('CvItems')
    if (!items) return null
    for (const child of Object.values(items)) {
      if (child?.state?.key === key) return child
    }
    return null
  }
  const scope = this.call('getCanvasScope')
  return scope?.selected
}

export function canvasGetSelectedKey() {
  if (this.call('isCanvasV2')) return this.call('getCanvasV2')?.selection?.primary || null
  const scope = this.call('getCanvasScope')
  return scope?.selectedKey
}

export function canvasGetHoveredKey() {
  const scope = this.call('getCanvasScope')
  return scope?.hoveredKey
}

// The shared chrome speaks the v1 mode names (inspectMode / panningMode
// / defaultMode). When v2 is active we map each to the v2 mode and also
// mirror the v1 flags on scope so any polling consumer stays in sync.
const V1_TO_V2 = {
  inspectMode: 'inspect',
  panningMode: 'pan',
  defaultMode: 'default',
  interactMode: 'interact'
}

export function canvasIsCanvasMode(mode) {
  if (this.call('isCanvasV2')) {
    const target = V1_TO_V2[mode]
    if (!target) return false
    return this.call('getCanvasV2')?.mode === target
  }
  const scope = this.call('getCanvasScope')
  return scope?.[mode] || false
}

export function canvasSetCanvasMode(mode) {
  if (this.call('isCanvasV2')) {
    const target = V1_TO_V2[mode] || mode
    this.call('setCanvasV2Mode', target)
    return
  }
  const scope = this.call('getCanvasScope')
  if (!scope) return
  scope.defaultMode = mode === 'defaultMode'
  scope.inspectMode = mode === 'inspectMode'
  scope.panningMode = mode === 'panningMode'
}

// (Removed 2026-06-01: `openModal('/settings')` canvas-v2 shim. The
// behavior is now inlined inside shared/methods/modal.js openRouterModal
// — the rename frees `openModal` for the @symbo.ls/windows API so
// dispatch isn't shadowed by this canvas-only handler. Callers updated
// to `openRouterModal('/settings')` automatically hit the same canvas-v2
// branch when they're on a canvas surface.)

// Bridges for CommandBar / Zoombar / CanvasContextMenu call sites that are
// DELIBERATELY dual-mode: written against the v1 `setWindow(key, val)` verb,
// but also want a v2-equivalent action when canvas v2 is active. Each routes
// the v1 verb to its v2 equivalent; every OTHER v1 `setWindow` call site
// (docs/explorer/componentEditor panels throughout shared/components/*,
// canvas's own legacy chrome, …) wants the REAL primitive
// (shared/methods/windows.js setWindow) and must never be repointed here.
//
// NAMED `bridgeSetWindow` (not `setWindow`) on purpose (Rule 63 fix,
// tickets/workspace.md "duplicate export names" #5, 2026-07-27): this used to
// be named `setWindow` too, which shadowed the real
// `shared/methods/windows.js` `setWindow` shell-wide — `eagerSlice.js` merges
// this file's exports into `context.methods` BEFORE shared's methods fold in
// via mergeSharedLibraries, and that fold only ADDS keys the target doesn't
// already have (@symbo.ls/utils mergeSharedLibraries → mergeOneLibrary), so
// canvas's narrow same-named shim permanently won and the real `setWindow`
// (openWindow/closeWindow/toggleWindow, `data[key] = val`, cookie + element
// sync) never ran anywhere canvas's methods were in scope — not just the
// unified shell, canvas's OWN standalone context.js merges the same way.
// Every non-bridge `setWindow` call site was silently dead until this rename.

// Zoombar keyboard icon → v2 shortcuts overlay. Falls back to no-op so
// non-v2 consumers don't break.
export function openKeyboardShortcuts() {
  if (this.call('isCanvasV2')) this.call('toggleShortcutsOverlay')
}

// `/open key`, CommandBar item click, Zoombar's "Docs" button, and
// CanvasContextMenu's componentEditor onChoose flow — explorer/componentEditor
// window verbs in v1. In v2, focus/fit the artboard instead. ONLY these
// specific documented callers should invoke this — see the file-header note.
export function bridgeSetWindow(window, arg) {
  if (this.call('isCanvasV2')) {
    if (window === 'explorer' && arg && typeof arg === 'string') {
      // `arg` may be a page/component key or `'search:<query>'`.
      if (arg === 'onboarding') {
        this.call('toggleShortcutsOverlay')
        return
      }
      if (arg.startsWith('search:')) return
      this.call('selectOne', arg)
      this.call('fitToSelection')
      this.call('clearSelection')
    }
    // In v2 canvas there is no code-editor or sidebar panel.
    // Map componentEditor requests to a fit-to-selection so the user gets
    // visual feedback (the artboard zooms into view) instead of a silent
    // no-op (#2244). The caller (CanvasContextMenu onChoose) calls
    // activateSelected(key) immediately after bridgeSetWindow — defer
    // fitToSelection one microtask so the selection is committed before we zoom.
    if (window === 'componentEditor') {
      const self = this
      Promise.resolve().then(() => {
        const key = self.call('getSelectedKey')
        if (key) self.call('fitToSelection')
      })
    }
  }
}

// SearchBar/CommandBar item open verbs.
export function canvasOpenArtboardItem(type, key) {
  if (this.call('isCanvasV2') && key) {
    this.call('selectOne', key)
    this.call('fitToSelection')
  }
}

export function canvasOpenItem(type, key) {
  this.call('openArtboardItem', type, key)
}
export function canvasActivateChosen(key, type) {
  this.call('openArtboardItem', type, key)
}
// v2 has no "functions" panel (that concept — FnEditNavbar/EditorPanels/
// ViewSvitcher — is v1-only file-sidebar UI, none of which canvas mounts any
// more; CanvasEditor.js nulls out every v1 chrome key). Correct-by-design
// no-op for v2; left alone (canvas methods fork spike, tickets/workspace.md
// #8, 2026-07-27). Verified zero live callers reach here today either way —
// shared/methods/crud/remove.js's `activateFunction(null)` call fires from
// the v1 file-tree delete path, which v2's own `deleteSelection` (canvas/
// functions/canvas/clipboard.js) never goes through.
export function canvasActivateFunction(_key) {
  /* no-op in v2 canvas */
}
// Already v2-aware by design (bridges the v1 `setSidebarContent(kind)` verb
// to the v2 settings panel toggle) — left alone (tickets/workspace.md #8).
// Note the v1 branch is currently a bare no-op (no fallback to shared's real
// async setSidebarContent): harmless today because every v1 call site
// (CanvasSearchItem/IslandNavbar/ActiveContentSwitcher/CanvasVerticalNavbar)
// resolves to a component that canvas no longer mounts (CanvasLegacyPage.js
// itself is orphaned), but if v1 file-sidebar chrome is ever revived this
// no-op will need a real v1 fallback branch.
export function canvasSetSidebarContent(_kind) {
  if (this.call('isCanvasV2')) this.call('toggleCanvasSettings')
}

// v2 fork of shared/methods/canvas.js activateContext — the ⋯ button on the
// selection navbar (SelectedNavbar.ContextBtn). The shared v1 body positions
// the legacy Playground ContextMenu (`this.getPlayground().lookdown(
// 'ContextMenu')`) — chrome canvas v2 never mounts — so on a v2 board the
// button THREW before opening anything (live-verified 2026-08-08 per Nika
// "icons clicking and functionality"). Route to the SAME house
// ContextDropdown the right-click path uses, seeded for the item, so the ⋯
// menu and right-click menu can never drift apart. No v1 fallback branch on
// purpose: every v1 call site is orphaned chrome inside this shell (see the
// activateFunction note above for the audit).
export function canvasActivateContext(ev, key) {
  if (!this.call('isCanvasV2')) return
  this.call('canvasStageContextmenu', ev, key || this.call('getSelectedKey'))
}

// v2 fork of shared/methods/component.js refreshComponentOnCanvas — the ⟳
// button on the selection navbar. The shared v1 body resolves the artboard
// via `findComponentOnCanvas` (v1 Collection walk) and warns "Cant find on
// canvas" on every v2 board. On v2 the honest refresh is a tile REMOUNT:
// pulse the item's membership out of and back into `_cvVisibleIds` — CvItems
// reconciles children BY KEY, so the tile unmounts and remounts, which boots
// a fresh embed of the page (same machinery viewport windowing uses; no
// reach-into-iframe internals). preventFetch on both writes — this must
// re-render the tile list, never re-fire the page's declarative fetches.
export function canvasRefreshComponentOnCanvas(key) {
  if (!this.call('isCanvasV2')) return
  const k = key || this.call('getSelectedKey')
  const root = this.state?.root || this.getRoot?.()?.state?.root
  const vis = root?._cvVisibleIds
  if (!k || !root?.update || !vis || !vis[k]) return
  const without = { ...vis }
  delete without[k]
  root.update({ _cvVisibleIds: without }, { preventFetch: true })
  const raf =
    typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb) => setTimeout(cb, 16)
  raf(() => {
    // Re-read live: the windowing recompute may have rewritten the set (and
    // possibly already re-added the key — the remount happened either way).
    const cur = root._cvVisibleIds || {}
    if (!cur[k]) root.update({ _cvVisibleIds: { ...cur, [k]: 1 } }, { preventFetch: true })
  })
}

// Theme toggle stub — delegates to scratch so config, CSS vars, and the
// scoped theme root stay in sync.
export function toggleTheme(target) {
  const doc = this.context?.document || document
  const cur = doc.documentElement.getAttribute('data-theme') || 'dark'
  const next = target || (cur === 'dark' ? 'light' : 'dark')
  changeGlobalTheme(next, this.context?.designSystem)
  canvasSyncCanvasBackgroundForTheme(next, doc)
}

// Generic cookie helper — Zoombar color picker uses this. Cheap impl;
// the editor likely has a real one elsewhere but local fallback keeps
// click-to-set working.
export function setCookie(name, value) {
  try {
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
      value
    )}; path=/; max-age=31536000`
  } catch {}
}

// syncCanvas() removed (tickets/opus.md ticket 2, 2026-08-09 investigation):
// it only ever bumped `scope._syncCounter`, a field with ONE write (this
// function) and ZERO reads anywhere in the monorepo — `git log -S
// "_syncCounter"` traces it to the v2 canvas "Phase 1" scaffold, stamped
// with the stated intent "trigger re-render of overlay by bumping a
// counter", but no reactive consumer was ever wired to read it (every
// apparent second source hit is a compiled dist bundle or a captured
// session-log artifact, not real code). All ~8 real call sites across the
// package were audited individually and either already had a working
// imperative re-render (refreshCanvasChrome) or explicit .update() call
// immediately adjacent, or a genuine reactive replacement (selection.js's
// `_cvSelectionTick`, added the day before this ticket) — none needed this.
// `syncCanvas` remains a real method on shared/methods/canvas.js (the v1
// multiplayer cursor/scope-broadcast implementation, `syncCanvas(data,
// opts)` — a different signature and purpose entirely); this package no
// longer shadows it (shared hands off to canvasGetCanvasCollection, see canvasMethodsOneOwner.test.js).
export function canvasGetCanvasCollection() {
  const root = this?.getRoot?.()
  const scene = root?.lookdown?.((el) => el.key === 'CanvasScene')
  return scene?.Collection || scene
}

// isEmbed / isEditMode / isReadOnly / isPlatform are NOT defined here: shared
// owns them (shared/methods/canvas.js, shared/methods/root.js) and canvas
// reads them through its shared library. The copies that lived here were
// the same logic restated, a second definition of each name in every bag
// that merges both (canvasMethodsOneOwner.test.js).

// getChosen / getChosenKey / getInContextKey — v2 has no "chosen" element
// concept (that's a v1-only idea: Chosen.js/ViewSvitcher.js/DocsView.js,
// none of which CanvasEditor.js mounts — `Chosen: null` — and updateOverlay.js,
// the only function that reads getInContextKey, is itself never invoked).
// Correct-by-design null for v2; left alone (tickets/workspace.md #8,
// 2026-07-27) — verified every call site resolves to a component canvas no
// longer mounts, so no live consumer gets a wrong answer today.
export function canvasGetChosen() {
  return null
}

export function canvasGetChosenKey() {
  return null
}

export function canvasGetInContextKey() {
  return null
}

// No getRootState / getRootContext here: both are FRAMEWORK element methods,
// and a library copy is refused (WS-SHARED-METHODS-SHADOW-FRAMEWORK-ELEMENT-
// METHODS-1; locked by packages/workspace/__tests__/
// sharedLibraryMethodsDoNotShadowBuiltins.test.js).
